/**
 * DesignReviewLanding — standalone `/design-review` route (RG10).
 *
 * Lists all real-estate projects with quick actions:
 *   - "Start Design Review" opens the workspace → Design Review tab
 *   - Shows recent EDO extraction + compliance score if run
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../lib/api";
import { Building2, ArrowRight, ShieldCheck, Sparkles } from "lucide-react";

export default function DesignReviewLanding() {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/projects");
        const arr = Array.isArray(r.data) ? r.data : (r.data?.projects || []);
        setProjects(arr.filter(p => p.domain === "real_estate"));
      } catch { setProjects([]); }
      finally { setLoading(false); }
    })();
  }, []);

  const openReview = (p) => {
    // Deep-link into the workspace with the Design Review tab pre-open
    navigate(`/projects/${p.id}?tab=design-review`);
  };

  return (
    <div style={{ padding: 40, background: "#0F172A", minHeight: "100vh",
                    color: "#F1F5F9" }}
         data-testid="design-review-landing">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <header style={{ marginBottom: 32 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <ShieldCheck size={32} style={{ color: "#22D3EE" }} />
            <h1 style={{ fontSize: 28, fontWeight: 700, margin: 0 }}>
              Design Review & Optimization
            </h1>
          </div>
          <p style={{ marginTop: 8, color: "#94A3B8", fontSize: 14,
                        maxWidth: 800 }}>
            Upload existing drawings and BOQs to get an instant compliance
            gap report, quality-preserving cost-saving suggestions, and a
            full Stage B–I redesign — all powered by Claude Opus 4.6.
          </p>
        </header>

        {loading && (
          <div data-testid="dr-landing-loading" style={{ color: "#94A3B8" }}>
            Loading your projects…
          </div>
        )}

        {!loading && projects.length === 0 && (
          <div data-testid="dr-landing-empty"
                style={{ padding: 40, background: "#1E293B",
                         border: "1px solid #334155", borderRadius: 8,
                         textAlign: "center" }}>
            <Building2 size={48} style={{ color: "#94A3B8",
                                              marginBottom: 12 }} />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              No real-estate projects yet.
            </div>
            <div style={{ color: "#94A3B8", fontSize: 13,
                            marginBottom: 20 }}>
              Create a real-estate project first, then come back to review
              its existing design documents.
            </div>
            <Link to="/projects" data-testid="dr-landing-create-btn"
                   style={{ padding: "10px 20px", background: "#22D3EE",
                            color: "#020617", borderRadius: 6,
                            textDecoration: "none", fontWeight: 600 }}>
              Go to Projects → Create New
            </Link>
          </div>
        )}

        {!loading && projects.length > 0 && (
          <div data-testid="dr-landing-project-list"
               style={{ display: "grid",
                          gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))",
                          gap: 20 }}>
            {projects.map(p => (
              <div key={p.id}
                   data-testid={`dr-landing-project-${p.id}`}
                   style={{ background: "#1E293B",
                              border: "1px solid #334155", borderRadius: 8,
                              padding: 20 }}>
                <div style={{ display: "flex", justifyContent: "space-between",
                                alignItems: "flex-start", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600,
                                    marginBottom: 4 }}>{p.name}</div>
                    <div style={{ fontSize: 11, color: "#94A3B8" }}>
                      {p.building_type || "Residential"} · {p.city || "—"},
                      &nbsp;{p.country || "—"}
                    </div>
                  </div>
                  <Building2 size={20} style={{ color: "#22D3EE" }} />
                </div>
                {p.design_review?.compliance_score && (
                  <div style={{ marginTop: 10, fontSize: 11, color: "#94A3B8" }}>
                    Last review: <b>{p.design_review.compliance_score.score_pct}%</b>
                    &nbsp;compliance
                  </div>
                )}
                <button data-testid={`dr-landing-review-btn-${p.id}`}
                         onClick={() => openReview(p)}
                         style={{ marginTop: 16, width: "100%",
                                  padding: "10px 12px",
                                  background: "#22D3EE", color: "#020617",
                                  border: "none", borderRadius: 4,
                                  fontWeight: 600, fontSize: 12,
                                  display: "inline-flex",
                                  justifyContent: "center", alignItems: "center",
                                  gap: 6, cursor: "pointer" }}>
                  <Sparkles size={12} />
                  Review Existing Design
                  <ArrowRight size={12} />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
